import { Injectable } from '@angular/core';

import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { AppStateService } from 'src/app/core/app-state.service';
import { BillReaderUser } from '../../shared/models';

const BILL_READER_AUTH_KEY = 'billReaderAuthenticateUser';
const BILL_READER_TOKEN_KEY = 'billReaderAuthToken';
const BILL_READER_TOKEN_EXPIRY_KEY = 'billReaderAuthTokenExpiry';
const TOKEN_EXPIRY_MS = 8 * 60 * 60 * 1000; // 8 hours

@Injectable({
	providedIn: 'root',
})
export class BillReaderAuthService {

	constructor(
		private toastrService: ToastrService,
		private router: Router,
		private appStateService: AppStateService,
		private modalService: BsModalService,
	) {}

	public getBillReaderAuthenticateUser(): BillReaderUser | null {
		if (this.isTokenExpired()) {
			this.clearBillReaderAuthenticateUser();
			return null;
		}
		const stored = localStorage.getItem(BILL_READER_AUTH_KEY);
		if (!stored) return null;
		try {
			const parsed = JSON.parse(stored);
			return new BillReaderUser(parsed);
		} catch {
			return null;
		}
	}

	public getBillReaderToken(): string | null {
		if (this.isTokenExpired()) {
			this.clearBillReaderAuthenticateUser();
			return null;
		}
		return localStorage.getItem(BILL_READER_TOKEN_KEY);
	}

	public setBillReaderAuthenticateUser(user: BillReaderUser, token: string): void {
		localStorage.setItem(BILL_READER_AUTH_KEY, JSON.stringify(user));
		localStorage.setItem(BILL_READER_TOKEN_KEY, token);
		localStorage.setItem(BILL_READER_TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_EXPIRY_MS));
	}

	public clearBillReaderAuthenticateUser(): void {
		localStorage.removeItem(BILL_READER_AUTH_KEY);
		localStorage.removeItem(BILL_READER_TOKEN_KEY);
		localStorage.removeItem(BILL_READER_TOKEN_EXPIRY_KEY);
	}

	private isTokenExpired(): boolean {
		const expiryStr = localStorage.getItem(BILL_READER_TOKEN_EXPIRY_KEY);
		if (!expiryStr) return true;
		const expiry = Number(expiryStr);
		return Number.isNaN(expiry) || Date.now() >= expiry;
	}

	public async logout(returnUrl?: string): Promise<boolean> {
		const activeToast = this.toastrService.info('Logging out...');
		this.clearBillReaderAuthenticateUser();
		this.hideAllModals();
		await this.router.navigate(['/bill-reader', 'login'], { queryParams: { returnUrl } });
		this.toastrService.remove(activeToast.toastId);
		this.appStateService.setReady({ app: false, direct: false, billReader: false, isPopupWindow: false });
		return true;
	}

	private hideAllModals(): void {
		// @ts-expect-error: Accessing private variable as workaround for missing feature
		this.modalService.loaders.forEach(loader => {
			loader.instance.hide();
		});
	}
}
